import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { AlertCircle, Loader2, Send } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  EMAIL_MAX_LENGTH,
  emailSchema,
  fieldError,
  type Student,
} from "@eduviaos/shared";
import { useInviteLearner } from "@/hooks/useLearnerInvites";

/**
 * Phase SC-4c — give one roster learner a login to the learner portal.
 *
 * A dialog rather than a one-tap mutation for one reason: the address.
 * Imported rows very often have no email, or carry a parent's, and an
 * invite is irreversible in the sense that it lands in somebody's inbox.
 * So the admin confirms where it's going, and if they type a new address
 * the BE writes it back onto the roster row — otherwise the next person
 * to look at this learner sees a blank email beside an invite that
 * plainly went somewhere.
 */
export interface LearnerInviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  student: Student | null;
  /** Copy hook: "learner" for a study centre, "student" elsewhere. */
  personNoun?: string;
  onInvited?: () => void;
}

export function LearnerInviteDialog({
  open,
  onOpenChange,
  student,
  personNoun = "learner",
  onInvited,
}: LearnerInviteDialogProps) {
  const reduceMotion = useReducedMotion();
  const mutation = useInviteLearner();

  const rosterEmail = student?.email ?? "";
  const [email, setEmail] = React.useState("");
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [showEmailError, setShowEmailError] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setEmail(rosterEmail);
      setSubmitError(null);
      setShowEmailError(false);
    }
  }, [open, rosterEmail]);

  const trimmed = email.trim();
  const emailError = fieldError(emailSchema, email);
  const visibleEmailError = showEmailError ? emailError : null;
  const canSubmit = !!student && !mutation.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setShowEmailError(true);
    if (!canSubmit || !student || emailError) return;
    try {
      await mutation.mutateAsync({
        student_id: student.id,
        // Only send an override when it differs, so an unchanged roster
        // address doesn't look like an edit in the audit trail.
        ...(trimmed.toLowerCase() === rosterEmail.toLowerCase() ? {} : { email: trimmed }),
      });
      toast.success(`Invite sent to ${trimmed}.`);
      onInvited?.();
      onOpenChange(false);
    } catch (err) {
      setSubmitError(
        err instanceof ApiError
          ? err.message || "Couldn't send that invite."
          : err instanceof Error
            ? err.message
            : "Couldn't send that invite.",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="learner-invite-dialog">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-primary/10 text-primary mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
            >
              <Send className="size-5" />
            </span>
            <div className="flex-1">
              <DialogTitle className="tracking-tight">Invite to the portal</DialogTitle>
              <DialogDescription className="mt-1 text-xs">
                {student?.name ?? `This ${personNoun}`} gets an email with a link to set their own
                password. Their seat, plans and review queue then live in their app.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form
          onSubmit={handleSubmit}
          noValidate
          className="space-y-4"
          data-testid="learner-invite-form"
        >
          <AnimatePresence>
            {submitError ? (
              <motion.div
                initial={reduceMotion ? false : { opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
                transition={{ duration: 0.16, ease: "easeOut" }}
                role="alert"
                className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
              >
                <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                <span>{submitError}</span>
              </motion.div>
            ) : null}
          </AnimatePresence>

          <div>
            <Label htmlFor="learner-invite-email" className="text-xs">
              Send to
              <span className="text-destructive ml-0.5">*</span>
            </Label>
            <Input
              id="learner-invite-email"
              type="email"
              inputMode="email"
              autoComplete="off"
              value={email}
              maxLength={EMAIL_MAX_LENGTH}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setShowEmailError(email.length > 0)}
              placeholder={`${personNoun}@example.com`}
              className="mt-1"
              aria-invalid={visibleEmailError ? true : undefined}
              aria-describedby={
                visibleEmailError ? "learner-invite-email-error" : "learner-invite-email-help"
              }
            />
            {visibleEmailError ? (
              <p id="learner-invite-email-error" className="text-destructive mt-1 text-xs">
                {visibleEmailError}
              </p>
            ) : null}
            <p id="learner-invite-email-help" className="text-muted-foreground mt-1 text-xs">
              {rosterEmail
                ? "Change it if this isn't their own address — the roster is updated to match."
                : `This ${personNoun} has no email on record. What you enter here is saved to their roster row.`}
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending ? (
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              ) : null}
              Send invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
